import { audioMime, base64ToUint8Array, pickMediaSourceCtor } from './tts/media-source-compat';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { distinctUntilChanged, map } from 'rxjs';
import { QRWebsocketService, QRWebsocketState, TtsAudioInit } from './qr-websocket.service';
import { DOCUMENT } from '@angular/common';
import { LanguageSettingsService } from './language-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tsMs } from './tts/tts-debug';
import { TtsInstanceLock } from './tts/tts-instance-lock';
import { TtsKeepWarm } from './tts/tts-keep-warm';
import { TtsMediaPipeline } from './tts/tts-media-pipeline';

/**
 * Plays the TTS read-along audio that the proxy emits as AAC-in-fMP4
 * over the guest WS. HTMLAudioElement + (Managed)MediaSource is the only
 * browser-audio path that survives screen lock on iOS Safari and
 * background tabs on Android Chrome.
 *
 * This service owns the protocol side: the WS subscribe/resubscribe state
 * machine, codec negotiation, and timing instrumentation. The DOM/MSE side
 * lives in TtsMediaPipeline, the iOS keep-warm tricks in TtsKeepWarm.
 */
@Injectable({ providedIn: 'root' })
export class TtsAudioService {
  public readonly enabled = signal(false);
  public readonly unsupported = signal(!pickMediaSourceCtor());

  private readonly ws = inject(QRWebsocketService);
  private readonly languageSettings = inject(LanguageSettingsService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly keepWarm = new TtsKeepWarm(this.document);
  private readonly pipeline = new TtsMediaPipeline(this.document);
  private readonly lock = new TtsInstanceLock(this.document);
  public readonly playing = this.pipeline.playing;

  private subscribed = false;
  private subscribedLang: string | null = null;

  private frameCount = 0;
  // performance.now() timestamps for measuring time-to-first-audio.
  private subscribeAt = 0;
  private audioInitAt = 0;

  constructor() {
    this.ws.audioInit$
      .pipe(takeUntilDestroyed())
      .subscribe((init) => this.handleAudioInit(init));
    this.ws.audioFrames$
      .pipe(takeUntilDestroyed())
      .subscribe((frame) => this.handleFrame(frame));

    this.languageSettings
      .getSelectedLanguage$()
      .pipe(
        map((lang) => lang?.languageCode),
        distinctUntilChanged(),
        takeUntilDestroyed(),
      )
      .subscribe((code) => {
        if (!code || !this.enabled() || this.subscribedLang === code) return;
        this.resubscribe(code);
      });

    // The proxy-side subscription dies with the socket, so `subscribed` only
    // means anything while the WS is OPEN. Reset it on any other state, and
    // on (re)open re-send the subscribe for the currently selected language —
    // this covers both transient reconnects and an enable that happened while
    // the socket was still connecting (where the first subscribe was dropped).
    // The media pipeline is deliberately left intact: the audio element was
    // unlocked by the enabling gesture, and the proxy re-sends an init
    // envelope that handleAudioInit treats as a mid-stream re-init.
    this.ws.state$
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        if (state !== QRWebsocketState.OPEN) {
          this.subscribed = false;
          this.subscribedLang = null;
          return;
        }
        if (!this.enabled() || this.unsupported() || this.subscribed) return;
        const lang = this.languageSettings.seletecedLanguage();
        if (!lang) return;
        console.log(`[TTS ${tsMs()}] ws open -> (re)subscribe ${lang.languageCode}`);
        this.subscribeWith(lang.languageCode);
      });

    // The audio-session fix stops a backgrounded page going silent, so a page
    // the guest has finished with keeps playing. Two open pages meant two
    // voices at once. Newest page wins; this one stands down.
    this.lock.onSuperseded(() => {
      if (!this.enabled()) {
        return;
      }
      console.log(`[TTS ${tsMs()}] superseded by a newer page, disabling read-along`);
      this.enabled.set(false);
      this.stop();
    });

    this.document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.pipeline.teardown();
      this.keepWarm.stop();
      this.lock.release();
    });
  }

  /** Must be called from a user gesture so the audio element is allowed to play on iOS. */
  setEnabled(value: boolean): void {
    if (value === this.enabled()) return;
    console.log(`[TTS ${tsMs()}] setEnabled(${value})`);
    this.enabled.set(value);
    if (value) {
      this.start();
    } else {
      this.stop();
    }
  }

  private start(): void {
    if (this.unsupported()) {
      console.warn('[TTS] MediaSource / ManagedMediaSource not supported');
      return;
    }
    const lang = this.languageSettings.seletecedLanguage();
    if (!lang) return;
    // Must run inside the enabling gesture: keeps the iOS audio output route
    // engaged so TTS playback never starts against a cold speaker.
    this.lock.claim();
    this.keepWarm.start();
    if (!this.pipeline.ensure()) {
      this.unsupported.set(true);
      this.keepWarm.stop();
      return;
    }
    this.subscribeWith(lang.languageCode);
  }

  private stop(): void {
    this.clearSubscription();
    this.pipeline.teardown();
    this.keepWarm.stop();
    this.lock.release();
  }

  private resubscribe(languageCode: string): void {
    this.clearSubscription();
    this.pipeline.teardown();
    if (!this.pipeline.ensure()) {
      this.unsupported.set(true);
      this.keepWarm.stop();
      return;
    }
    this.subscribeWith(languageCode);
  }

  private clearSubscription(): void {
    if (this.subscribed) this.ws.sendTtsUnsubscribe();
    this.subscribed = false;
    this.subscribedLang = null;
  }

  private subscribeWith(languageCode: string): void {
    this.subscribeAt = performance.now();
    this.audioInitAt = 0;
    const sent = this.ws.sendTtsSubscribe(languageCode);
    console.log(`[TTS ${tsMs()}] sendTtsSubscribe(${languageCode}) sent=${sent}`);
    this.subscribed = sent;
    this.subscribedLang = sent ? languageCode : null;
  }

  private handleAudioInit(init: TtsAudioInit): void {
    this.audioInitAt = performance.now();
    const sinceSub = this.subscribeAt ? `${(this.audioInitAt - this.subscribeAt).toFixed(0)}ms after subscribe` : 'no subscribe ts';
    console.log(`[TTS ${tsMs()}] audio_init (${sinceSub}) codec=${init.codec} head=${init.head_b64?.length ?? 0}b64 ${this.pipeline.snap()}`);
    if (!this.enabled() || this.unsupported()) return;
    const mime = audioMime(init.codec);
    const MSCtor = pickMediaSourceCtor();
    if (!MSCtor || !MSCtor.isTypeSupported(mime)) {
      console.warn('[TTS] MediaSource cannot play', mime);
      this.unsupported.set(true);
      return;
    }
    if (!init.head_b64) {
      console.warn('[TTS] init envelope missing head_b64 (fMP4 init segment)');
      return;
    }
    let header: Uint8Array;
    try {
      header = base64ToUint8Array(init.head_b64);
    } catch (err) {
      console.warn('[TTS] init envelope head_b64 is not valid base64', err);
      return;
    }
    this.frameCount = 0;
    this.pipeline.acceptInit(mime, header);
  }

  private handleFrame(frame: ArrayBuffer): void {
    if (!this.enabled() || this.unsupported()) return;
    const chunk = new Uint8Array(frame);
    this.frameCount++;
    if (this.frameCount === 1) {
      const now = performance.now();
      const sinceSub = this.subscribeAt ? `${(now - this.subscribeAt).toFixed(0)}ms after subscribe` : '?';
      const sinceInit = this.audioInitAt ? `${(now - this.audioInitAt).toFixed(0)}ms after audio_init` : '?';
      console.log(`[TTS ${tsMs()}] TIME-TO-FIRST-FRAME: ${sinceSub}, ${sinceInit}`);
    }
    this.pipeline.acceptFrame(chunk, this.frameCount);
  }

  private handleVisibilityChange = (): void => {
    if (this.document.hidden) return;
    if (!this.enabled()) return;
    this.keepWarm.resume();
    this.pipeline.resumeIfPaused();
  };
}
